/**
 * Inline hint shown when the URL the user entered cannot be captured by the
 * server-side proxy — localhost, private IPs, anti-bot blocked sites, or pure
 * SPAs that render entirely in the browser. Tells the user to use the
 * OverlayQA extension instead.
 */
interface ExtensionCaptureHintProps {
  reason: 'localhost' | 'private' | 'blocked' | 'spa' | 'local-file';
}

const REASON_COPY: Record<ExtensionCaptureHintProps['reason'], string> = {
  'local-file':
    "Local files can't be captured from our servers. Use the OverlayQA extension to capture this page directly from your browser.",
  localhost:
    "We can't reach localhost from our servers. Use the OverlayQA extension to capture this page directly from your browser.",
  private:
    "We can't reach private/internal network addresses. Use the OverlayQA extension instead.",
  blocked:
    'Use the OverlayQA extension to bypass anti-bot protection and capture this page directly.',
  spa: "We'll capture a snapshot since this site renders entirely in the browser. For interactive review, use the extension on a live tab.",
};

export function ExtensionCaptureHint({ reason }: ExtensionCaptureHintProps) {
  return (
    <div
      className="rounded-lg bg-primary-50 border border-primary-100 p-3 text-xs text-gray-700 mt-2"
      data-testid="extension-capture-hint"
    >
      <div className="font-medium text-primary-700 mb-1">Use the extension instead</div>
      {REASON_COPY[reason]}
    </div>
  );
}
